import { useCallback, useEffect, useMemo, useState } from 'react';
import { FileSpreadsheet, Loader2, Truck, CalendarClock, Calculator, Info } from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import { demurrageService, type DemurrageReport, type DemurrageReportRow } from '../services/demurrageService';
import { warehouseService } from '../services/warehouseService';

const formatDateTimeTr = (value: string | null | undefined) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatMoney = (value: number | string | null | undefined) => {
  const numericValue = Number(value || 0);
  return `${numericValue.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`;
};

const EMPTY_REPORT: DemurrageReport = {
  settings: { transitionDate: '', dailyFee: 0, geofenceKm: 2, customerCutoffTime: '13:00', depotCutoffTime: '13:00', active: false },
  summary: { shipmentCount: 0, eligibleCount: 0, ineligibleCount: 0, totalDays: 0, totalAmount: 0 },
  details: [],
  byFacility: [],
  byCity: [],
  byDistrict: [],
};

export function YatmaRaporu() {
  const today = useMemo(() => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }, []);
  const monthStart = useMemo(() => `${today.slice(0, 8)}01`, [today]);

  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(today);
  const [warehouseId, setWarehouseId] = useState('');
  const [plate, setPlate] = useState('');
  const [warehouses, setWarehouses] = useState<Array<{ id: string | number; name: string }>>([]);
  const [report, setReport] = useState<DemurrageReport>(EMPTY_REPORT);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState<'detail' | 'facility' | 'city' | 'district'>('detail');

  useEffect(() => {
    warehouseService.getWarehouses()
      .then((response: any) => {
        const list = Array.isArray(response?.data) ? response.data : [];
        setWarehouses(list.map((item: any) => ({ id: item.id ?? item._id, name: item.name || item.company_name || '-' })));
      })
      .catch(() => {});
  }, []);

  const handleGenerate = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await demurrageService.getReport({ from, to, warehouseId: warehouseId || undefined, plate: plate.trim() || undefined });
      setReport(response?.data || EMPTY_REPORT);
      setLoaded(true);
    } catch (err: any) {
      console.error('Yatma raporu yüklenemedi:', err);
      setError(err?.message || 'Rapor oluşturulurken bir hata oluştu');
      setReport(EMPTY_REPORT);
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [from, to, warehouseId, plate]);

  useEffect(() => {
    void handleGenerate();
  }, [handleGenerate]);

  const exportExcel = () => {
    const workbook = XLSX.utils.book_new();

    const detailSheet = XLSX.utils.json_to_sheet(report.details.map((row) => ({
      'Sevkiyat No': row.shipmentNo,
      'Plaka': row.plate,
      'Sürücü': row.driverName,
      'Tesis': row.facilityName,
      'Müşteri / Depo': row.deliveryName,
      'İl': row.city,
      'İlçe': row.district,
      'Varış Zamanı': formatDateTimeTr(row.arrivalAt),
      'Tahliye Zamanı': formatDateTimeTr(row.unloadingAt),
      'Yatma Günü': row.nDays,
      'Tutar (TL)': row.totalAmount,
      'Durum': row.status,
      'Not': row.notes,
    })));
    detailSheet['!cols'] = [{ wch: 14 }, { wch: 14 }, { wch: 24 }, { wch: 28 }, { wch: 28 }, { wch: 14 }, { wch: 14 }, { wch: 17 }, { wch: 17 }, { wch: 10 }, { wch: 12 }, { wch: 14 }, { wch: 26 }];
    XLSX.utils.book_append_sheet(workbook, detailSheet, 'Yatma Raporu');

    const facilitySheet = XLSX.utils.json_to_sheet(report.byFacility.map((row) => ({
      'Tesis': row.key,
      'Sevkiyat Sayısı': row.count,
      'Toplam Yatma Günü': row.totalDays,
      'Toplam Tutar (TL)': row.totalAmount,
    })));
    XLSX.utils.book_append_sheet(workbook, facilitySheet, 'Tesis Bazında Özet');

    const citySheet = XLSX.utils.json_to_sheet(report.byCity.map((row) => ({
      'İl': row.key,
      'Sevkiyat Sayısı': row.count,
      'Toplam Yatma Günü': row.totalDays,
      'Toplam Tutar (TL)': row.totalAmount,
    })));
    XLSX.utils.book_append_sheet(workbook, citySheet, 'İl Bazında Özet');

    const districtSheet = XLSX.utils.json_to_sheet(report.byDistrict.map((row) => ({
      'İlçe': row.key,
      'Sevkiyat Sayısı': row.count,
      'Toplam Yatma Günü': row.totalDays,
      'Toplam Tutar (TL)': row.totalAmount,
    })));
    XLSX.utils.book_append_sheet(workbook, districtSheet, 'İlçe Bazında Özet');

    XLSX.writeFile(workbook, `yatma-raporu-${from}-${to}.xlsx`);
  };

  const summaryCards = [
    { label: 'Toplam Sevkiyat', value: String(report.summary.shipmentCount), icon: Truck, color: 'bg-blue-50 text-blue-700' },
    { label: 'Toplam Yatma Günü', value: report.summary.totalDays.toLocaleString('tr-TR', { maximumFractionDigits: 2 }), icon: CalendarClock, color: 'bg-amber-50 text-amber-700' },
    { label: 'Toplam Yatma Bedeli', value: formatMoney(report.summary.totalAmount), icon: Calculator, color: 'bg-emerald-50 text-emerald-700' },
  ];

  const tabs: Array<{ key: 'detail' | 'facility' | 'city' | 'district'; label: string }> = [
    { key: 'detail', label: 'Detay' },
    { key: 'facility', label: 'Tesis Bazında' },
    { key: 'city', label: 'İl Bazında' },
    { key: 'district', label: 'İlçe Bazında' },
  ];

  const renderSummaryTable = (rows: Array<{ key: string; count: number; totalDays: number; totalAmount: number }>) => (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-[#141414]">
      <table className="w-full text-left text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900 dark:text-slate-400">
          <tr>
            <th className="px-4 py-3 font-semibold">Ad</th>
            <th className="px-4 py-3 text-right font-semibold">Sevkiyat Sayısı</th>
            <th className="px-4 py-3 text-right font-semibold">Toplam Yatma Günü</th>
            <th className="px-4 py-3 text-right font-semibold">Toplam Tutar</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {rows.length === 0 ? (
            <tr>
              <td colSpan={4} className="px-4 py-8 text-center text-slate-400">Kayıt bulunamadı</td>
            </tr>
          ) : rows.map((row, index) => (
            <tr key={`${row.key}-${index}`} className="hover:bg-slate-50 dark:hover:bg-slate-900">
              <td className="px-4 py-2.5 font-medium text-slate-800 dark:text-slate-100">{row.key}</td>
              <td className="px-4 py-2.5 text-right text-slate-600 dark:text-slate-300">{row.count}</td>
              <td className="px-4 py-2.5 text-right text-slate-600 dark:text-slate-300">{row.totalDays.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}</td>
              <td className="px-4 py-2.5 text-right font-semibold text-slate-800 dark:text-slate-100">{formatMoney(row.totalAmount)}</td>
            </tr>
          ))}
          {rows.length > 0 && (
            <tr className="bg-slate-50 font-semibold dark:bg-slate-900">
              <td className="px-4 py-3 text-slate-800 dark:text-slate-100">Genel Toplam</td>
              <td className="px-4 py-3 text-right text-slate-800 dark:text-slate-100">{rows.reduce((sum, row) => sum + row.count, 0)}</td>
              <td className="px-4 py-3 text-right text-slate-800 dark:text-slate-100">{rows.reduce((sum, row) => sum + row.totalDays, 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}</td>
              <td className="px-4 py-3 text-right text-slate-800 dark:text-slate-100">{formatMoney(rows.reduce((sum, row) => sum + row.totalAmount, 0))}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const renderDetailTable = () => (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-[#141414]">
      <table className="w-full text-left text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900 dark:text-slate-400">
          <tr>
            <th className="px-4 py-3 font-semibold">Sevkiyat No</th>
            <th className="px-4 py-3 font-semibold">Plaka</th>
            <th className="px-4 py-3 font-semibold">Sürücü</th>
            <th className="px-4 py-3 font-semibold">Tesis</th>
            <th className="px-4 py-3 font-semibold">Müşteri / Depo</th>
            <th className="px-4 py-3 font-semibold">İl / İlçe</th>
            <th className="px-4 py-3 font-semibold">Varış Zamanı</th>
            <th className="px-4 py-3 font-semibold">Tahliye Zamanı</th>
            <th className="px-4 py-3 text-right font-semibold">Yatma Günü</th>
            <th className="px-4 py-3 text-right font-semibold">Tutar</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {report.details.length === 0 ? (
            <tr>
              <td colSpan={10} className="px-4 py-8 text-center text-slate-400">Kayıt bulunamadı</td>
            </tr>
          ) : report.details.map((row: DemurrageReportRow, index: number) => (
            <tr key={`${row.shipmentId}-${index}`} className="hover:bg-slate-50 dark:hover:bg-slate-900">
              <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">{row.shipmentNo}</td>
              <td className="px-4 py-2.5 font-medium text-slate-800 dark:text-slate-100">{row.plate}</td>
              <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">{row.driverName}</td>
              <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">{row.facilityName}</td>
              <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">{row.deliveryName}</td>
              <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">{row.city} / {row.district}</td>
              <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">{formatDateTimeTr(row.arrivalAt)}</td>
              <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">{formatDateTimeTr(row.unloadingAt)}</td>
              <td className="px-4 py-2.5 text-right">
                <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${row.nDays > 0 ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300' : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'}`}>
                  {row.nDays}
                </span>
              </td>
              <td className="px-4 py-2.5 text-right font-semibold text-slate-800 dark:text-slate-100">{formatMoney(row.totalAmount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-slate-100">Yatma Raporu</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
            Spot sevkiyatlar için geofence ve kesim saati kurallarına göre otomatik hesaplanan yatma bedeli raporu.
          </p>
        </div>
        <button
          type="button"
          onClick={exportExcel}
          disabled={loading || report.details.length === 0}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
        >
          <FileSpreadsheet className="h-4 w-4" />
          Excele Aktar
        </button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-[#141414]">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">Başlangıç Tarihi</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200" />
          </div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">Bitiş Tarihi</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200" />
          </div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">Tesis</label>
            <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
              <option value="">Tümü</option>
              {warehouses.map((warehouse) => (
                <option key={String(warehouse.id)} value={String(warehouse.id)}>{warehouse.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">Plaka</label>
            <input type="text" value={plate} onChange={(e) => setPlate(e.target.value)} placeholder="Örn: 34 ABC 123" className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200" />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={() => void handleGenerate()}
              disabled={loading}
              className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />}
              Rapor Oluştur
            </button>
          </div>
        </div>
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-blue-50 p-3 text-xs text-blue-800 dark:bg-blue-950/50 dark:text-blue-300">
          <Info className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            Hesaplama kuralı: Araç teslim noktasının {report.settings.geofenceKm} km geofence alanına girdiği zaman varış kabul edilir.
            İlk gün için müşteri kesim saati {report.settings.customerCutoffTime}, depo kesim saati {report.settings.depotCutoffTime}. Günlük birim bedel: {formatMoney(report.settings.dailyFee)}.
            {!report.settings.active ? ' Geçiş tarihi henüz işaretlenmediği veya gelmediği için rapor bilgi amaçlıdır.' : ''}
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        {summaryCards.map((card) => (
          <div key={card.label} className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-[#141414]">
            <div className={`flex h-11 w-11 items-center justify-center rounded-lg ${card.color}`}>
              <card.icon className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">{card.label}</div>
              <div className="text-xl font-bold text-slate-900 dark:text-slate-100">{card.value}</div>
            </div>
          </div>
        ))}
      </div>

      {loaded && (
        <>
          <div className="flex gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${activeTab === tab.key ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-300'}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'detail' && renderDetailTable()}
          {activeTab === 'facility' && renderSummaryTable(report.byFacility)}
          {activeTab === 'city' && renderSummaryTable(report.byCity)}
          {activeTab === 'district' && renderSummaryTable(report.byDistrict)}
        </>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12 text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      )}
    </div>
  );
}
